import { FileSourceEnum } from '@systemeio/file-manager'
import { ButtonProps, FormInput, Hint } from '@systemeio/ui'
import EmailTemplateSelectorNewEditorModal from 'modules/email/components/email-template-selector/email-template-selector-modal'
import { MailingId } from 'modules/mailing/types/mailing-interface'
import { useEffect, useState } from 'react'
import DOMPurify from 'isomorphic-dompurify'
import ButtonLikeLink from 'shared/components/button-like-link'
import Confirmation from 'shared/components/confirmation-modal'
import { SubstituteInterface } from 'shared/components/dashboard-mail-editor/components/substitutes-block'
import {
  MailingPoolStatusColors,
  MailingPoolStatusHelpMessages,
} from 'shared/components/dashboard-mail-editor/enums/mailing-pool-status-enum'
import { useEmailOptions } from 'shared/components/dashboard-mail-editor/hooks/use-email-options'
import { getEmailStatusLink } from 'shared/components/dashboard-mail-editor/utils/get-email-status-link'
import { isWithRecipient } from 'shared/components/dashboard-mail-editor/utils/is-with-recipients'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import TransLoco from 'shared/components/trans-loco'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { twMerge } from 'tailwind-merge'
import { DashboardMailEditorCommonProps, ExtendedState } from '../../types/common'
import { NewEditor } from './new-editor'
import { MagicWandIcon } from '@systemeio/ui/icons'
import { emailTextExtensions, TipTapEditor } from '@systemeio/tip-tap'
import { useSubstitutes } from '../../hooks/use-substitutes'
import { useEmailFooterData } from '../../hooks/use-email-footer-data'

export type EditorBlockProps<T extends ExtendedState> = Pick<
  DashboardMailEditorCommonProps<T>,
  'isPreFetching' | 'inModal' | 'errors' | 'tempState' | 'onChangeEmail' | 'disabled'
> & { withRecipient?: boolean } & {
  isSwitchEditorButtonShown?: boolean
  switchEditorButtonProps?: ButtonProps
  isEditorEditable?: boolean
  showSettingsLink?: boolean
  mailingId?: MailingId
  additionalSubstitutes?: SubstituteInterface[]
  className?: string
}

const EditorBlock = <T extends ExtendedState>({
  disabled,
  errors,
  inModal,
  isEditorEditable = true,
  isPreFetching,
  isSwitchEditorButtonShown = false,
  onChangeEmail,
  showSettingsLink = true,
  tempState,
  withRecipient,
  mailingId,
  additionalSubstitutes,
  className,
}: EditorBlockProps<T>) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()

  const { data: options } = useEmailOptions()

  const [isTemplateModalOpened, setIsTemplateModalOpened] = useState<boolean>(false)
  const [isConfirmationModalOpened, setIsConfirmationModalOpened] = useState<boolean>(false)

  const onChooseTemplateModal = async (templateId: number) => {
    tempState && (await onChangeEmail({ ...tempState, useNewEditor: true, template: templateId }))
    setIsTemplateModalOpened(false)
  }
  const onConfirmationModalOpened = async () => {
    tempState && (await onChangeEmail({ ...tempState, useNewEditor: !tempState.useNewEditor }))
    setIsConfirmationModalOpened(false)
  }

  const { substitutionData } = useSubstitutes(additionalSubstitutes)

  const [emailFooterDataContent, setEmailFooterDataContent] = useState<string | undefined>()

  const { emailFooterData } = useEmailFooterData()

  useEffect(() => {
    if (tempState?.fromEmail) {
      const domain = tempState.fromEmail.split('@')[1]
      if (emailFooterData) {
        setEmailFooterDataContent(
          emailFooterData.items.find(item => item.domainName === domain)?.content,
        )
      }
    }
  }, [tempState?.fromEmail, emailFooterData])

  return (
    <div
      className={twMerge(
        `bg-background-secondary flex w-full flex-col gap-5 rounded-md`,
        !inModal && 'p-5',
        tempState?.useNewEditor && 'h-[800px] lg:h-fit',
        className,
      )}
    >
      {withRecipient && isWithRecipient(withRecipient, tempState) && (
        <FormInput
          disabled={disabled}
          isPreFetching={isPreFetching}
          label={t('dashboard.email.recipient_address')}
          required
          value={tempState?.recipient || ''}
          error={errors.recipient}
          onChange={e =>
            tempState && onChangeEmail({ ...(tempState as T), recipient: e.target.value })
          }
        />
      )}
      <FormInput
        disabled={disabled}
        isPreFetching={isPreFetching}
        label={
          user &&
          options && (
            <Hint
              color={MailingPoolStatusColors[options.emailPool]}
              label={
                <TransLoco
                  t={t}
                  i18nKey={MailingPoolStatusHelpMessages[options.emailPool]}
                  components={{
                    a: (
                      <a
                        href={getEmailStatusLink(user?.dashboardLocale)}
                        target={'_blank'}
                        rel={'noreferrer'}
                        className={'primary-link'}
                      />
                    ),
                  }}
                />
              }
              popoverClassName={'w-[300px] sm:w-[500px]'}
            >
              {t('dashboard.email.subject')}
            </Hint>
          )
        }
        required
        value={tempState?.subject || ''}
        onChange={e => tempState && onChangeEmail({ ...tempState, subject: e.target.value })}
        error={errors.subject}
      />
      <FormInput
        disabled={disabled}
        isPreFetching={isPreFetching}
        label={
          user &&
          options && (
            <Hint label={t('dashboard.email.preview_text_tooltip')}>
              {t('dashboard.email.preview_text_label')}
            </Hint>
          )
        }
        value={tempState?.previewText || ''}
        error={errors.previewText}
        onChange={e => tempState && onChangeEmail({ ...tempState, previewText: e.target.value })}
      />
      <div className={`flex basis-full flex-col gap-1 text-sm font-medium`}>
        <div className="flex justify-between">
          <span>{t('dashboard.email.body')}</span>
          {isSwitchEditorButtonShown && !isPreFetching && (
            <>
              <ButtonLikeLink
                disabled={disabled}
                onClick={() => {
                  if (tempState) {
                    if (!tempState.useNewEditor && !tempState.html) {
                      !tempState.useNewEditor && setIsTemplateModalOpened(true)
                    } else {
                      setIsConfirmationModalOpened(true)
                    }
                  }
                }}
                className="gap-1"
              >
                <MagicWandIcon className={'h-[14]px w-[14px]'} />
                {tempState?.useNewEditor
                  ? t('dashboard.email.switch_to_the_classic_editor')
                  : t('dashboard.email.switch_to_the_visual_editor')}
              </ButtonLikeLink>
            </>
          )}
        </div>
        <div className="relative h-full">
          {tempState?.useNewEditor && mailingId ? (
            <NewEditor
              tempState={tempState}
              isPreFetching={isPreFetching}
              mailingId={mailingId}
              disabled={disabled || !isEditorEditable}
            />
          ) : (
            <div>
              <TipTapEditor
                extensions={emailTextExtensions}
                substitutions={substitutionData}
                isPreFetching={isPreFetching}
                isEditable={!disabled && isEditorEditable}
                content={tempState?.body || undefined}
                onChange={data => tempState && onChangeEmail({ ...tempState, body: data })}
                fileSource={FileSourceEnum.Email}
                className={twMerge(
                  emailFooterDataContent && 'rounded-b-none',
                  errors.common.length !== 0 && 'border-danger',
                )}
              />
              {emailFooterDataContent && (
                <div className={'border-text-secondary/30 rounded-b-lg border border-t-0 p-2'}>
                  <div
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(emailFooterDataContent) }}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showSettingsLink && (
        <span className={'mt-2'}>
          <TransLoco
            t={t}
            i18nKey={'dashboard.email.change_footer_link'}
            components={{
              a: (
                <LinkWithoutPrefetch
                  href={'/profile/mailing-settings'}
                  className={'primary-link'}
                />
              ),
            }}
          />
        </span>
      )}
      {errors.body && <p className="text-danger text-sm">{errors.body}</p>}
      <Confirmation
        opened={isConfirmationModalOpened}
        onClose={() => setIsConfirmationModalOpened(false)}
        onConfirm={onConfirmationModalOpened}
        confirmationText={
          tempState?.useNewEditor
            ? t('dashboard.email.switch_to_the_classic_editor_message')
            : t('dashboard.email.switch_to_the_visual_editor_message')
        }
      />
      <EmailTemplateSelectorNewEditorModal
        opened={isTemplateModalOpened}
        onClose={() => setIsTemplateModalOpened(false)}
        onChoose={onChooseTemplateModal}
      />
    </div>
  )
}
export default EditorBlock
